import { useState, useEffect, useCallback, useMemo } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useAuthFetch } from '../lib/useAuthFetch'
import { Card, StatTile, Pill, SectionLabel, Sparkline, DetailPanel, DecisionSummary } from '../components/ui'
import type { PillTone } from '../components/ui'

const API = import.meta.env.VITE_API_URL ?? ''

// ─── Types (mirror /api/v1/competitors/:id/profile) ─────────────────────────
interface Location { id: string; name: string }
interface Range { min: number | null; max: number | null }
interface SharedSku {
  product: string; category: string
  their_price: number; your_price: number | null
  gap: number | null; dir: 'negative' | 'positive' | 'zero' | 'na'
  on_sale: boolean; original_price: number | null; discount_label: string | null
}
interface SharedBrand {
  brand: string; brand_key: string
  your_skus: number; their_skus: number
  your_range: Range; their_range: Range
  avg_gap: number | null
  posture: 'they_lower' | 'you_lower' | 'even' | 'unknown'
  has_markdown: boolean
  trend: number[]
  skus: SharedSku[]
}
interface CategoryRow {
  category: string
  your_skus: number; your_range: Range
  their_skus: number; their_range: Range
  shared_brands: number
}
interface Exclusive { brand: string; sku_count: number; categories: string[] }
interface Markdown {
  product: string; brand: string | null; category: string
  original_price: number; price: number; pct_off: number; dollar_off: number
  discount_label: string | null
  your_price: number | null; vs_you_dollar: number | null; rival_cheaper: boolean
}
interface Headline {
  total_skus: number; shared_brands: number
  your_exclusive_brands: number; their_exclusive_brands: number
  active_markdowns: number; avg_price_gap: number | null; skus_they_undercut: number
}
interface ProfileData {
  rival: {
    id: string; name: string; distance_mi: number | null
    last_scraped: string | null; is_blocked: boolean; blocked_at: string | null; total_skus: number
  }
  location: { id: string; name: string }
  has_own_menu: boolean
  headline: Headline
  shared_brands: SharedBrand[]
  category_overview: CategoryRow[]
  your_exclusives: Exclusive[]
  their_exclusives: Exclusive[]
  markdowns: Markdown[]
}

interface DataAccessState {
  status: 'available' | 'temporarily_unavailable'
  last_observed_at: string | null
  request_pending: boolean
}

type Tab = 'shared' | 'category' | 'gaps' | 'markdowns'

// ─── Formatters ─────────────────────────────────────────────────────────────
const fmtMoney = (n: number) => '$' + n.toLocaleString('en-US', { maximumFractionDigits: 2 })
const fmtRange = (r: Range) =>
  r.min == null || r.max == null ? '—' : r.min === r.max ? fmtMoney(r.min) : `${fmtMoney(r.min)}–${fmtMoney(r.max)}`
const fmtGap = (g: number | null) =>
  g == null ? '—' : Math.abs(g) < 0.005 ? 'Even' : (g < 0 ? '−' : '+') + '$' + Math.abs(g).toLocaleString('en-US', { maximumFractionDigits: 2 })
function timeAgo(iso: string | null): string {
  if (!iso) return 'never'
  const ms = Date.now() - new Date(iso).getTime()
  if (Number.isNaN(ms)) return 'unknown'
  const m = Math.floor(ms / 60000)
  if (m < 1) return 'just now'
  if (m < 60) return `${m} min ago`
  const h = Math.floor(m / 60)
  if (h < 24) return `${h}h ago`
  return `${Math.floor(h / 24)}d ago`
}

// gap → dir color (their − your; negative = they undercut you)
const dirColor = (dir: SharedSku['dir']) =>
  dir === 'negative' ? 'var(--danger)' : dir === 'positive' ? 'var(--accent)' : 'var(--text-3)'
const postureLabel: Record<SharedBrand['posture'], string> = {
  they_lower: 'they undercut you',
  you_lower: 'you undercut them',
  even: 'at parity',
  unknown: 'no matched SKUs',
}

// ─── Small self-contained location dropdown (matches MarkdownsView) ──────────
function LocationPicker({ locations, value, onChange }: {
  locations: Location[]; value: string | null; onChange: (id: string) => void
}) {
  const [open, setOpen] = useState(false)
  if (locations.length <= 1) return null
  const current = locations.find(l => l.id === value)?.name ?? 'Select'
  return (
    <div style={{ position: 'relative' }} onMouseLeave={() => setOpen(false)}>
      <button
        onClick={() => setOpen(v => !v)}
        style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 8, padding: '6px 12px', fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--text-1)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' }}
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: 11, height: 11, color: 'var(--accent)' }}><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" /></svg>
        {current}
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ width: 9, height: 9, opacity: 0.55 }}><polyline points="6 9 12 15 18 9" /></svg>
      </button>
      {open && (
        <div style={{ position: 'absolute', top: 'calc(100% + 4px)', right: 0, zIndex: 200, background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 8, minWidth: 200, maxHeight: 320, overflowY: 'auto', boxShadow: '0 8px 24px rgba(0,0,0,0.3)' }}>
          {locations.map(l => (
            <div key={l.id}
              onClick={() => { onChange(l.id); setOpen(false) }}
              style={{ padding: '8px 12px', fontSize: 11, cursor: 'pointer', color: l.id === value ? 'var(--accent)' : 'var(--text-1)', background: l.id === value ? 'rgba(9,161,161,0.08)' : 'transparent', borderLeft: l.id === value ? '2px solid var(--accent)' : '2px solid transparent' }}
            >{l.name}</div>
          ))}
        </div>
      )}
    </div>
  )
}

// ─── Centered status block (loading / error / empty) ────────────────────────
function CenterState({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 10, padding: 40, textAlign: 'center' }}>
      {children}
    </div>
  )
}

// ─── Component ───────────────────────────────────────────────────────────────
export default function CompetitorProfile() {
  const navigate = useNavigate()
  const { rivalId } = useParams<{ rivalId: string }>()
  const [searchParams] = useSearchParams()
  const authFetch = useAuthFetch()

  const [locations, setLocations] = useState<Location[]>([])
  const [selectedLocation, setSelectedLocation] = useState<string | null>(null)
  const [data, setData] = useState<ProfileData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [notTracked, setNotTracked] = useState(false)
  const [dataAccess, setDataAccess] = useState<DataAccessState | null>(null)
  const [dataAccessLoading, setDataAccessLoading] = useState(true)
  const [requestingAccess, setRequestingAccess] = useState(false)
  const [requestError, setRequestError] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState<Tab>('shared')
  const [selectedBrand, setSelectedBrand] = useState<string | null>(null)

  // Load org locations + the rival's actively-tracked locations once, then seed the
  // picker to a location where the rival IS tracked so opening it never lands on
  // "Not tracked here": URL ?location_id= (if a valid org location) → saved pick (if
  // still tracked) → a location where it's tracked → first org location.
  useEffect(() => {
    if (!rivalId) return
    const urlLoc = searchParams.get('location_id')
    Promise.all([
      authFetch(`${API}/api/v1/locations`)
        .then(r => r.ok ? r.json() : r.json().then((d: any) => { throw new Error(d.error || `HTTP ${r.status}`) })),
      authFetch(`${API}/api/v1/competitors/${rivalId}/tracked-locations`)
        .then(r => (r.ok ? r.json() : null)).catch(() => null),
    ])
      .then(([locData, trackedData]: any[]) => {
        const locs: Location[] = locData.locations || []
        setLocations(locs)
        if (!locs.length) { setLoading(false); return }
        const isOrgLoc = (id: string | null) => !!id && locs.some(l => l.id === id)
        const trackedIds: string[] = (trackedData?.data?.locations || [])
          .map((l: any) => l.id).filter(isOrgLoc)
        const saved = localStorage.getItem('competitor_profile_location')
        const pick =
          (isOrgLoc(urlLoc) ? urlLoc : null) ??
          (saved && trackedIds.includes(saved) ? saved : null) ??
          trackedIds[0] ??
          locs[0].id
        setSelectedLocation(pick)
      })
      .catch(e => { setError(`Locations: ${e?.message || 'Network error'}`); setLoading(false) })
  }, [authFetch, rivalId, searchParams])

  const load = useCallback(() => {
    if (!rivalId || !selectedLocation) return
    setLoading(true); setError(null); setNotTracked(false)
    authFetch(`${API}/api/v1/competitors/${rivalId}/profile?location_id=${selectedLocation}`)
      .then(async r => {
        if (r.status === 404) {
          const d = await r.json().catch(() => ({}))
          if (d.code === 'RIVAL_NOT_TRACKED') { setNotTracked(true); setData(null); return null }
          throw new Error(d.error || 'Not found')
        }
        if (!r.ok) { const d = await r.json().catch(() => ({})); throw new Error(d.error || `HTTP ${r.status}`) }
        return r.json()
      })
      .then((d: any) => { if (d) setData(d.data as ProfileData) })
      .catch(e => setError(e?.message || 'Network error'))
      .finally(() => setLoading(false))
  }, [rivalId, selectedLocation, authFetch])

  useEffect(() => { load() }, [load])

  useEffect(() => {
    if (!rivalId || !selectedLocation) return
    let active = true
    setDataAccess(null)
    setDataAccessLoading(true)
    authFetch(`${API}/api/v1/competitors/${rivalId}/data-access?location_id=${selectedLocation}`)
      .then(async r => r.ok ? r.json() : null)
      .then((payload: any) => {
        if (active) setDataAccess(payload?.data ?? null)
      })
      .catch(() => { if (active) setDataAccess(null) })
      .finally(() => { if (active) setDataAccessLoading(false) })
    return () => { active = false }
  }, [authFetch, rivalId, selectedLocation])

  // Default-select the first shared brand whenever a new dataset arrives.
  useEffect(() => {
    if (data?.shared_brands.length) setSelectedBrand(data.shared_brands[0].brand_key)
    else setSelectedBrand(null)
  }, [data])

  const selectLocation = (id: string) => {
    setSelectedLocation(id)
    localStorage.setItem('competitor_profile_location', id)
  }

  const requestAccess = async () => {
    if (!rivalId || !selectedLocation || requestingAccess || dataAccess?.request_pending) return
    setRequestingAccess(true)
    setRequestError(null)
    try {
      const response = await authFetch(`${API}/api/v1/competitors/${rivalId}/data-access/request?location_id=${selectedLocation}`, {
        method: 'POST',
      })
      const payload = await response.json().catch(() => ({}))
      if (!response.ok) throw new Error(payload.error || 'Request failed')
      setDataAccess(current => ({
        status: 'temporarily_unavailable',
        last_observed_at: current?.last_observed_at ?? null,
        request_pending: true,
      }))
    } catch (err: any) {
      setRequestError(err?.message || 'Request failed')
    } finally {
      setRequestingAccess(false)
    }
  }

  const selectedBrandData = useMemo(
    () => data?.shared_brands.find(b => b.brand_key === selectedBrand) ?? null,
    [data, selectedBrand]
  )

  // ── Loading / error / empty gates ────────────────────────────────────────
  if (loading && !data) {
    return (
      <Shell locationPicker={<LocationPicker locations={locations} value={selectedLocation} onChange={selectLocation} />} onBack={() => navigate(-1)}>
        <CenterState>
          <div style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--text-3)' }}>Pulling this rival's latest menu…</div>
        </CenterState>
      </Shell>
    )
  }
  if (error) {
    return (
      <Shell locationPicker={<LocationPicker locations={locations} value={selectedLocation} onChange={selectLocation} />} onBack={() => navigate(-1)}>
        <CenterState>
          <div style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--danger)' }}>Couldn't load this rival's profile.</div>
          <div style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--text-3)', background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 6, padding: '8px 14px', maxWidth: 480 }}>{error}</div>
          <button onClick={load} style={{ marginTop: 6, fontFamily: 'var(--sans)', fontSize: 12, fontWeight: 600, padding: '6px 14px', borderRadius: 'var(--r-sm)', border: '1px solid var(--accent)', background: 'var(--accent)', color: '#fff', cursor: 'pointer' }}>Retry</button>
        </CenterState>
      </Shell>
    )
  }
  if (notTracked) {
    return (
      <Shell locationPicker={<LocationPicker locations={locations} value={selectedLocation} onChange={selectLocation} />} onBack={() => navigate(-1)}>
        <CenterState>
          <div style={{ fontFamily: 'var(--display)', fontSize: 22, color: 'var(--text-1)' }}>Not tracked here</div>
          <div style={{ fontSize: 13, color: 'var(--text-2)', maxWidth: 460, lineHeight: 1.55 }}>
            You aren't actively tracking this rival at {locations.find(l => l.id === selectedLocation)?.name ?? 'this location'}.
            {locations.length > 1 ? ' Switch locations above, or track this rival to build a full comparison.' : ' Track this rival to build a full comparison.'}
          </div>
          <button onClick={() => navigate('/today')} style={{ marginTop: 6, fontFamily: 'var(--sans)', fontSize: 12, fontWeight: 600, padding: '7px 16px', borderRadius: 'var(--r-sm)', border: '1px solid var(--border-2)', background: 'var(--surface-2)', color: 'var(--text-1)', cursor: 'pointer' }}>Back to Today</button>
        </CenterState>
      </Shell>
    )
  }
  if (!data) {
    return (
      <Shell locationPicker={<LocationPicker locations={locations} value={selectedLocation} onChange={selectLocation} />} onBack={() => navigate(-1)}>
        <CenterState><div style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--text-3)' }}>Select a location to profile this rival.</div></CenterState>
      </Shell>
    )
  }

  const { rival, headline } = data
  const lastObservedAt = dataAccess?.last_observed_at ?? rival.last_scraped
  const gapsCount = headline.your_exclusive_brands + headline.their_exclusive_brands
  const largestGapBrand = data.shared_brands
    .filter(brand => brand.avg_gap != null && brand.avg_gap < -0.5)
    .sort((a, b) => (a.avg_gap ?? 0) - (b.avg_gap ?? 0))[0] ?? null

  const tabs: { id: Tab; label: string; count?: number }[] = [
    { id: 'shared', label: 'Shared Brands', count: headline.shared_brands },
    { id: 'category', label: 'Category Overview' },
    { id: 'gaps', label: 'Brand Gaps', count: gapsCount },
    { id: 'markdowns', label: 'Markdowns', count: headline.active_markdowns },
  ]

  return (
    <Shell
      locationPicker={<LocationPicker locations={locations} value={selectedLocation} onChange={selectLocation} />}
      onBack={() => navigate(-1)}
    >
      {/* ── Rival header ── */}
      <div style={{ background: 'var(--surface)', borderBottom: '1px solid var(--border)', flexShrink: 0, padding: '0 24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '14px 0 12px' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
              <span style={{ fontFamily: 'var(--display)', fontSize: 26, fontWeight: 400, letterSpacing: '-0.015em', color: 'var(--text-1)', lineHeight: 1 }}>{rival.name}</span>
              <Pill tone="tracking" variant="chip" style={{ letterSpacing: '0.1em', textTransform: 'uppercase' }}>
                Rival
              </Pill>
            </div>
            <div style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--text-3)', letterSpacing: '0.04em' }}>
              {rival.distance_mi != null ? `${rival.distance_mi} mi · ` : ''}
              {dataAccessLoading
                ? 'Checking menu freshness…'
                : dataAccess?.status === 'temporarily_unavailable'
                  ? `Last successful menu ${timeAgo(lastObservedAt)} · ${rival.total_skus.toLocaleString('en-US')} historical SKUs`
                  : dataAccess?.status === 'available'
                    ? `Last updated ${timeAgo(lastObservedAt)} · ${rival.total_skus.toLocaleString('en-US')} SKUs`
                    : 'Menu freshness unavailable · Current comparisons paused'}
            </div>
          </div>

        </div>
      </div>

      {dataAccess?.status === 'temporarily_unavailable' && (
        <div
          data-testid="competitor-data-unavailable"
          style={{ margin: '14px 24px 0', padding: '12px 14px', borderRadius: 10, border: '1px solid var(--border-2)', background: 'var(--surface-2)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap', flexShrink: 0 }}
        >
          <div style={{ minWidth: 220, flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-1)' }}>Menu data is temporarily unavailable</div>
            <div style={{ marginTop: 3, fontSize: 11.5, lineHeight: 1.45, color: 'var(--text-2)' }}>
              Request access and CannaSpy will investigate this competitor's public menu source.
            </div>
            {requestError && <div style={{ marginTop: 4, fontSize: 10.5, color: 'var(--danger)' }}>{requestError}</div>}
          </div>
          <button
            data-testid="request-competitor-access"
            onClick={requestAccess}
            disabled={requestingAccess || dataAccess.request_pending}
            style={{ padding: '8px 13px', borderRadius: 8, border: '1px solid var(--accent)', background: dataAccess.request_pending ? 'var(--surface-3)' : 'var(--accent)', color: dataAccess.request_pending ? 'var(--text-2)' : '#fff', fontSize: 11.5, fontWeight: 700, cursor: dataAccess.request_pending ? 'default' : 'pointer' }}
          >
            {dataAccess.request_pending ? 'Request received' : requestingAccess ? 'Sending…' : 'Request Access'}
          </button>
        </div>
      )}

      {dataAccessLoading ? (
        <div data-testid="competitor-profile-freshness-loading" style={{ margin: 24, padding: 18, borderRadius: 10, border: '1px solid var(--border)', background: 'var(--surface-2)', color: 'var(--text-2)', fontSize: 12 }}>
          Checking menu freshness before showing pricing intelligence…
        </div>
      ) : !dataAccess ? (
        <div data-testid="competitor-profile-freshness-unknown" style={{ margin: 24, padding: 18, borderRadius: 10, border: '1px solid var(--border)', background: 'var(--surface-2)' }}>
          <SectionLabel>Menu freshness unavailable</SectionLabel>
          <div style={{ marginTop: 8, fontSize: 12.5, lineHeight: 1.55, color: 'var(--text-2)' }}>
            Current comparisons are paused because CannaSpy couldn't confirm when this menu was last observed. Refresh this page to check again.
          </div>
        </div>
      ) : dataAccess.status === 'temporarily_unavailable' ? (
        <div data-testid="competitor-profile-historical" style={{ margin: 24, padding: 18, borderRadius: 10, border: '1px solid var(--border)', background: 'var(--surface-2)' }}>
          <SectionLabel>Historical menu snapshot</SectionLabel>
          <div style={{ marginTop: 8, fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--text-3)' }}>
            Last observed {timeAgo(lastObservedAt)} · {rival.total_skus.toLocaleString('en-US')} historical SKUs
          </div>
          <div style={{ marginTop: 10, fontSize: 13, fontWeight: 700, color: 'var(--text-1)' }}>
            This information is for reference only. It is not current pricing intelligence.
          </div>
          <div style={{ marginTop: 5, fontSize: 12, lineHeight: 1.55, color: 'var(--text-2)' }}>
            Current comparisons and recommendations are paused until CannaSpy confirms a fresh public menu.
          </div>
        </div>
      ) : (
        <>
          {data.has_own_menu && (
            <div style={{ padding: '12px 24px', background: 'var(--bg)', flexShrink: 0 }}>
              <DecisionSummary
            className="competitor-profile-summary"
            question="Where is this rival creating the most pricing pressure?"
            priority={headline.skus_they_undercut > 0 ? 'critical' : 'watch'}
            answer={largestGapBrand
              ? `${largestGapBrand.brand} has the widest observed gap at ${fmtGap(largestGapBrand.avg_gap)} on average`
              : `${rival.name} is not currently undercutting you on matched products`}
            change={`Current profile updated ${timeAgo(rival.last_scraped)}. Prior-scan movement is not available on this page.`}
            impact={headline.avg_price_gap != null
              ? `${rival.name} is lower on ${headline.skus_they_undercut} matched SKUs and ${fmtMoney(Math.abs(headline.avg_price_gap))} lower on average. Revenue and margin exposure are not available, so this is a pricing-pressure signal.`
              : 'Comparable-price impact is unavailable because the profile has no matched-price average.'}
            provenance={{
              updatedAt: rival.last_scraped ? new Date(rival.last_scraped).toLocaleString() : 'unavailable',
              source: 'current rival menu compared with your location menu',
              confidence: 'unavailable',
            }}
            action={largestGapBrand ? (
              <button
                data-testid="competitor-profile-review-priority"
                onClick={() => { setActiveTab('shared'); setSelectedBrand(largestGapBrand.brand_key) }}
                style={{ padding: '9px 13px', borderRadius: 9, border: '1px solid var(--accent)', background: 'var(--accent)', color: '#fff', fontSize: 11.5, fontWeight: 700, cursor: 'pointer', fontFamily: 'var(--sans)' }}
              >
                Review {largestGapBrand.brand} →
              </button>
            ) : undefined}
              />
            </div>
          )}

          {/* ── Tabs ── */}
          <div style={{ display: 'flex', borderBottom: '2px solid var(--border)', padding: '0 24px', background: 'var(--surface)', flexShrink: 0 }}>
            {tabs.map(tab => (
              <div key={tab.id} onClick={() => setActiveTab(tab.id)}
                style={{ padding: '10px 18px', fontSize: 12, fontWeight: 600, color: activeTab === tab.id ? 'var(--accent)' : 'var(--text-3)', cursor: 'pointer', borderBottom: activeTab === tab.id ? '2px solid var(--accent)' : '2px solid transparent', marginBottom: -2, whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 6 }}
              >
                {tab.label}
                {tab.count != null && (
                  <span style={{ fontFamily: 'var(--mono)', fontSize: 9, background: activeTab === tab.id ? 'var(--accent-soft)' : 'var(--surface-3)', border: `1px solid ${activeTab === tab.id ? 'rgba(9,161,161,0.2)' : 'var(--border)'}`, borderRadius: 10, padding: '1px 6px', color: activeTab === tab.id ? 'var(--accent)' : 'var(--text-3)' }}>{tab.count}</span>
                )}
              </div>
            ))}
          </div>

          {/* ── Tab content ── */}
          <div style={{ flex: 1, overflow: 'hidden', display: 'flex', minHeight: 0 }}>
            {activeTab === 'shared' && (
              <SharedBrandsTab
                data={data}
                selectedBrand={selectedBrandData}
                selectedKey={selectedBrand}
                onSelect={setSelectedBrand}
                onPriceMatrix={() => navigate('/prices')}
              />
            )}
            {activeTab === 'category' && <CategoryTab rows={data.category_overview} hasOwn={data.has_own_menu} />}
            {activeTab === 'gaps' && <BrandGapsTab data={data} />}
            {activeTab === 'markdowns' && <MarkdownsTab data={data} />}
          </div>
        </>
      )}
    </Shell>
  )
}

// ─── Shell (back bar + location picker + children) ──────────────────────────
function Shell({ children, onBack, locationPicker }: { children: React.ReactNode; onBack: () => void; locationPicker: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: 'var(--bg)', color: 'var(--text-1)', fontFamily: 'var(--sans)', fontSize: 14, overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '10px 24px', borderBottom: '1px solid var(--border)', background: 'var(--surface)', flexShrink: 0 }}>
        <button onClick={onBack}
          style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '5px 10px', borderRadius: 8, border: '1px solid var(--border-2)', background: 'var(--surface-2)', color: 'var(--text-2)', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--sans)' }}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="12" height="12"><polyline points="15 18 9 12 15 6" /></svg>
          Back
        </button>
        {locationPicker}
      </div>
      {children}
    </div>
  )
}

// ─── Shared Brands tab ──────────────────────────────────────────────────────
function SharedBrandsTab({ data, selectedBrand, selectedKey, onSelect, onPriceMatrix }: {
  data: ProfileData; selectedBrand: SharedBrand | null; selectedKey: string | null
  onSelect: (k: string) => void; onPriceMatrix: () => void
}) {
  if (!data.has_own_menu) {
    return (
      <CenterState>
        <div style={{ fontFamily: 'var(--display)', fontSize: 20, color: 'var(--text-1)' }}>No own menu on file</div>
        <div style={{ fontSize: 12.5, color: 'var(--text-2)', maxWidth: 460, lineHeight: 1.55 }}>
          We don't have your own menu for {data.location.name} yet, so shared-brand comparisons aren't available.
          Their catalogue is in <strong>Category Overview</strong> and <strong>Brand Gaps</strong>.
        </div>
      </CenterState>
    )
  }
  if (!data.shared_brands.length) {
    return (
      <CenterState>
        <div style={{ fontFamily: 'var(--display)', fontSize: 20, color: 'var(--text-1)' }}>No shared brands</div>
        <div style={{ fontSize: 12.5, color: 'var(--text-2)', maxWidth: 460, lineHeight: 1.55 }}>
          You and this rival don't currently carry any of the same brands. See <strong>Brand Gaps</strong> for what each of you carries exclusively.
        </div>
      </CenterState>
    )
  }

  const alerts = data.shared_brands.filter(b => b.posture === 'they_lower' || b.has_markdown)
  const calm = data.shared_brands.filter(b => !(b.posture === 'they_lower' || b.has_markdown))

  return (
    <>
      {/* Brand list */}
      <div style={{ width: 340, flexShrink: 0, borderRight: '1px solid var(--border)', display: 'flex', flexDirection: 'column', height: '100%', background: 'var(--surface)' }}>
        <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)', flexShrink: 0 }}>
          <SectionLabel style={{ marginBottom: 6 }}>Brands both dispensaries carry</SectionLabel>
          <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
            <Pill tone="accent" variant="chip">{data.headline.shared_brands} shared</Pill>
            <Pill tone="neutral" variant="chip">{data.headline.your_exclusive_brands} yours</Pill>
            <Pill tone="blocked-by" variant="chip">{data.headline.their_exclusive_brands} theirs</Pill>
          </div>
        </div>
        <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
          {alerts.length > 0 && <BrandDivider>⚠ Attention — action needed</BrandDivider>}
          {alerts.map(b => <BrandRow key={b.brand_key} brand={b} isAlert selected={selectedKey === b.brand_key} onSelect={() => onSelect(b.brand_key)} />)}
          {calm.length > 0 && <BrandDivider>Shared brands — no active undercut</BrandDivider>}
          {calm.map(b => <BrandRow key={b.brand_key} brand={b} isAlert={false} selected={selectedKey === b.brand_key} onSelect={() => onSelect(b.brand_key)} />)}
        </div>
      </div>

      {/* Brand detail */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', height: '100%', background: 'var(--surface-2)', minWidth: 0 }}>
        {selectedBrand
          ? <BrandDetail brand={selectedBrand} rivalName={data.rival.name} onPriceMatrix={onPriceMatrix} />
          : <CenterState><div style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--text-3)' }}>Select a brand to compare SKUs</div></CenterState>}
      </div>
    </>
  )
}

function BrandDivider({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ padding: '6px 16px 4px', fontFamily: 'var(--mono)', fontSize: 9, fontWeight: 500, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--text-3)', background: 'var(--surface-2)', borderBottom: '1px solid var(--border)', position: 'sticky', top: 0, zIndex: 2 }}>
      {children}
    </div>
  )
}

function BrandRow({ brand, isAlert, selected, onSelect }: { brand: SharedBrand; isAlert: boolean; selected: boolean; onSelect: () => void }) {
  const initials = brand.brand.replace(/[^a-zA-Z0-9 ]/g, '').split(/\s+/).map(w => w[0]).join('').substring(0, 2).toUpperCase()
  return (
    <div onClick={onSelect}
      style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 16px', borderBottom: '1px solid var(--border)', cursor: 'pointer', background: selected ? 'var(--accent-soft)' : isAlert ? 'var(--danger-soft)' : 'transparent', borderLeft: selected ? '3px solid var(--accent)' : isAlert ? '3px solid var(--danger)' : '3px solid transparent' }}
    >
      <div style={{ width: 32, height: 32, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontFamily: 'var(--mono)', fontSize: 10, fontWeight: 700, background: isAlert ? 'var(--danger-soft)' : 'var(--accent-soft)', color: isAlert ? 'var(--danger)' : 'var(--accent)' }}>
        {initials || '—'}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-1)', marginBottom: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{brand.brand}</div>
        <div style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--text-2)' }}>
          {brand.their_skus} their SKU{brand.their_skus === 1 ? '' : 's'} · {postureLabel[brand.posture]}{brand.has_markdown ? ' · markdown' : ''}
        </div>
      </div>
      <div style={{ fontFamily: 'var(--mono)', fontSize: 12, fontWeight: 700, flexShrink: 0, color: brand.avg_gap == null ? 'var(--text-3)' : brand.avg_gap < -0.5 ? 'var(--danger)' : brand.avg_gap > 0.5 ? 'var(--accent)' : 'var(--text-3)' }}>
        {fmtGap(brand.avg_gap)}
      </div>
    </div>
  )
}

function BrandDetail({ brand, rivalName, onPriceMatrix }: { brand: SharedBrand; rivalName: string; onPriceMatrix: () => void }) {
  return (
    <div data-testid="competitor-profile-brand-detail" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div style={{ padding: '16px 20px 12px', borderBottom: '1px solid var(--border)', flexShrink: 0, background: 'var(--surface)' }}>
        <DetailPanel eyebrow={`Shared brand · ${rivalName}`} title={brand.brand} style={{ marginBottom: 4 }}>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: -8 }}>
            <StatTile size="sm" value={String(brand.your_skus)} label="Your SKUs" />
            <StatTile size="sm" value={String(brand.their_skus)} label="Their SKUs" />
            <StatTile size="sm" value={fmtRange(brand.your_range)} label="Your range" />
            <StatTile size="sm" value={fmtRange(brand.their_range)} label="Their range" />
          </div>
        </DetailPanel>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px', minHeight: 0 }}>
        {brand.posture === 'they_lower' && (
          <div style={{ background: 'var(--danger-soft)', border: '1px solid rgba(224,90,106,0.2)', borderRadius: 'var(--r-sm)', padding: '10px 12px', marginBottom: 14 }}>
            <div style={{ fontFamily: 'var(--mono)', fontSize: 9, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--danger)', marginBottom: 3 }}>They undercut you</div>
            <div style={{ fontSize: 12, color: 'var(--text-1)' }}>
              On matched SKUs this rival averages {fmtGap(brand.avg_gap)} versus your price. You're higher on the products below marked in coral.
            </div>
          </div>
        )}

        <SectionLabel style={{ marginBottom: 8 }}>SKU-level price comparison</SectionLabel>
        <div style={{ overflowX: 'auto', marginBottom: 18 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
            <thead>
              <tr>
                {['Product', 'Your price', 'Their price', 'Gap'].map((h, i) => (
                  <th key={h} style={{ fontFamily: 'var(--mono)', fontSize: 8.5, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--text-3)', padding: '5px 8px', borderBottom: '2px solid var(--border)', textAlign: i === 0 ? 'left' : 'right', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {brand.skus.map((sku, i) => (
                <tr key={sku.product + i}>
                  <td style={{ padding: '8px', borderBottom: '1px solid var(--border)', color: 'var(--text-1)' }}>{sku.product}</td>
                  <td style={{ padding: '8px', borderBottom: '1px solid var(--border)', fontFamily: 'var(--mono)', textAlign: 'right', color: sku.your_price != null ? 'var(--text-1)' : 'var(--text-3)' }}>
                    {sku.your_price != null ? fmtMoney(sku.your_price) : '—'}
                  </td>
                  <td style={{ padding: '8px', borderBottom: '1px solid var(--border)', fontFamily: 'var(--mono)', textAlign: 'right' }}>
                    {sku.on_sale && sku.original_price != null ? (
                      <span>
                        <span style={{ textDecoration: 'line-through', color: 'var(--text-3)', fontSize: 10, marginRight: 4 }}>{fmtMoney(sku.original_price)}</span>
                        <span style={{ color: 'var(--danger)', fontWeight: 700 }}>{fmtMoney(sku.their_price)}</span>
                      </span>
                    ) : (
                      <span style={{ color: dirColor(sku.dir), fontWeight: 700 }}>{fmtMoney(sku.their_price)}</span>
                    )}
                  </td>
                  <td style={{ padding: '8px', borderBottom: '1px solid var(--border)', textAlign: 'right' }}>
                    {sku.gap == null ? (
                      <span style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--text-3)' }}>—</span>
                    ) : (
                      <Pill tone={sku.dir === 'negative' ? 'danger' : sku.dir === 'positive' ? 'accent' : 'neutral'} variant="chip" style={{ fontWeight: 700 }}>{fmtGap(sku.gap)}</Pill>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* 30-day trend — real history, honest degrade */}
        <Card variant="sm" style={{ padding: '10px 12px' }}>
          <SectionLabel style={{ marginBottom: 6 }}>30-day price trend · this rival</SectionLabel>
          {brand.trend.length >= 2 ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
              <Sparkline points={brand.trend} width={180} height={40} />
              <div style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--text-3)' }}>
                {fmtMoney(brand.trend[0])} → {fmtMoney(brand.trend[brand.trend.length - 1])} · avg brand price / day
              </div>
            </div>
          ) : (
            <div style={{ fontSize: 11, color: 'var(--text-2)' }}>
              Building price history — we need a few more daily snapshots of {brand.brand} at this rival before a trend line is meaningful.
            </div>
          )}
        </Card>
      </div>

      <div style={{ padding: '12px 20px', borderTop: '1px solid var(--border)', flexShrink: 0, background: 'var(--surface)', display: 'flex', gap: 8 }}>
        <button onClick={onPriceMatrix}
          style={{ flex: 1, padding: '9px 14px', borderRadius: 'var(--r-sm)', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--sans)', border: '1.5px solid var(--accent)', background: 'var(--accent)', color: '#fff' }}
        >View in price matrix →</button>
      </div>
    </div>
  )
}

// ─── Category Overview tab ──────────────────────────────────────────────────
function CategoryTab({ rows, hasOwn }: { rows: CategoryRow[]; hasOwn: boolean }) {
  if (!rows.length) {
    return <CenterState><div style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--text-3)' }}>No priced menu data for this rival yet.</div></CenterState>
  }
  return (
    <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px' }}>
      <Card style={{ padding: 0, overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                {['Category', 'Your menu', 'Their menu', 'Coverage', 'Shared brands'].map((h, i) => (
                  <th key={h} style={{ fontFamily: 'var(--mono)', fontSize: 9, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--text-3)', padding: '10px 16px', borderBottom: '2px solid var(--border)', textAlign: i >= 3 ? 'right' : 'left', background: 'var(--surface-2)', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map(c => {
                const max = Math.max(c.your_skus, c.their_skus, 1)
                return (
                  <tr key={c.category}>
                    <td style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)', fontSize: 13, fontWeight: 700, color: 'var(--text-1)' }}>{c.category}</td>
                    <td style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)' }}>
                      <div style={{ fontFamily: 'var(--mono)', fontSize: 11, fontWeight: 600, color: hasOwn ? 'var(--text-1)' : 'var(--text-3)' }}>{hasOwn ? `${c.your_skus} SKUs` : '—'}</div>
                      {hasOwn && <div style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--text-3)', marginTop: 1 }}>{fmtRange(c.your_range)}</div>}
                    </td>
                    <td style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)' }}>
                      <div style={{ fontFamily: 'var(--mono)', fontSize: 11, fontWeight: 600, color: 'var(--text-1)' }}>{c.their_skus} SKUs</div>
                      <div style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--text-3)', marginTop: 1 }}>{fmtRange(c.their_range)}</div>
                    </td>
                    <td style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)', width: 130 }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        {hasOwn && (
                          <div>
                            <div style={{ fontFamily: 'var(--mono)', fontSize: 8, color: 'var(--accent)', marginBottom: 1 }}>YOU</div>
                            <div style={{ height: 5, borderRadius: 3, background: 'var(--accent)', width: `${Math.round((c.your_skus / max) * 100)}%`, minWidth: c.your_skus > 0 ? 4 : 0 }} />
                          </div>
                        )}
                        <div>
                          <div style={{ fontFamily: 'var(--mono)', fontSize: 8, color: 'var(--rose)', marginBottom: 1 }}>THEM</div>
                          <div style={{ height: 5, borderRadius: 3, background: 'var(--rose)', width: `${Math.round((c.their_skus / max) * 100)}%`, minWidth: c.their_skus > 0 ? 4 : 0 }} />
                        </div>
                      </div>
                    </td>
                    <td style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)', fontFamily: 'var(--mono)', fontSize: 11, fontWeight: 700, textAlign: 'right', color: c.shared_brands > 0 ? 'var(--accent)' : 'var(--text-3)' }}>
                      {c.shared_brands > 0 ? `${c.shared_brands} brand${c.shared_brands === 1 ? '' : 's'}` : 'None'}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  )
}

// ─── Brand Gaps tab ─────────────────────────────────────────────────────────
function BrandGapsTab({ data }: { data: ProfileData }) {
  return (
    <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, alignContent: 'start' }}>
      <GapCard
        badge="Your exclusives" badgeTone="accent"
        title="Brands you carry — they don't"
        sub="Protect these. This rival carries none of them — your competitive advantage."
        items={data.your_exclusives}
        dot="var(--accent)"
        empty={data.has_own_menu ? 'You carry no brands this rival is missing.' : 'No own menu on file for this location yet.'}
      />
      <GapCard
        badge="Their exclusives" badgeTone="blocked-by"
        title="Brands they carry — you don't"
        sub="Watch list. If any of these gain traction, consider adding them to your menu."
        items={data.their_exclusives}
        dot="var(--warm)"
        empty="This rival carries no brands you're missing."
      />
    </div>
  )
}

function GapCard({ badge, badgeTone, title, sub, items, dot, empty }: {
  badge: string; badgeTone: PillTone; title: string; sub: string; items: Exclusive[]; dot: string; empty: string
}) {
  return (
    <Card style={{ padding: 0, overflow: 'hidden' }}>
      <div style={{ padding: '14px 16px 10px', borderBottom: '1px solid var(--border)', background: 'var(--surface-2)' }}>
        <Pill tone={badgeTone} variant="chip" style={{ marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.1em' }}>{badge}</Pill>
        <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-1)', marginBottom: 3 }}>{title}</div>
        <div style={{ fontSize: 11, color: 'var(--text-2)', lineHeight: 1.5 }}>{sub}</div>
      </div>
      <div style={{ padding: '8px 0' }}>
        {items.length === 0 ? (
          <div style={{ padding: '18px 16px', fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--text-3)' }}>{empty}</div>
        ) : items.map(item => (
          <div key={item.brand} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 16px', borderBottom: '1px solid var(--border)' }}>
            <div style={{ width: 8, height: 8, borderRadius: '50%', background: dot, flexShrink: 0 }} />
            <div style={{ flex: 1, fontSize: 12, fontWeight: 600, color: 'var(--text-1)', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.brand}</div>
            <div style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--text-3)', textAlign: 'right', whiteSpace: 'nowrap' }}>
              {item.sku_count} SKU{item.sku_count === 1 ? '' : 's'}{item.categories.length ? ` · ${item.categories.slice(0, 2).join(', ')}` : ''}
            </div>
          </div>
        ))}
      </div>
    </Card>
  )
}

// ─── Markdowns tab ──────────────────────────────────────────────────────────
function MarkdownsTab({ data }: { data: ProfileData }) {
  if (!data.markdowns.length) {
    return (
      <CenterState>
        <div style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--text-3)', maxWidth: 420, lineHeight: 1.6 }}>
          No active markdowns at this rival right now. Every SKU on their menu is at its regular price.
        </div>
      </CenterState>
    )
  }
  return (
    <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px' }}>
      <div style={{ fontSize: 11, color: 'var(--text-2)', lineHeight: 1.55, marginBottom: 14, maxWidth: 640 }}>
        SKUs whose current menu price sits below their regular price. Observed facts only — we don't assert why a price dropped.
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {data.markdowns.map((m, i) => (
          <Card key={m.product + i} style={{ padding: '12px 14px', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text-1)', marginBottom: 3 }}>{m.product}</div>
              <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                {m.brand && <Pill tone="neutral" variant="chip">{m.brand}</Pill>}
                <Pill tone="neutral" variant="chip">{m.category}</Pill>
                {m.discount_label && <Pill tone="blocked-by" variant="chip">{m.discount_label}</Pill>}
              </div>
            </div>
            <div style={{ fontFamily: 'var(--mono)', fontSize: 12, whiteSpace: 'nowrap' }}>
              <span style={{ color: 'var(--text-3)', textDecoration: 'line-through' }}>{fmtMoney(m.original_price)}</span>
              <span style={{ color: 'var(--text-3)', margin: '0 4px' }}>→</span>
              <span style={{ color: 'var(--text-1)', fontWeight: 700 }}>{fmtMoney(m.price)}</span>
            </div>
            <Pill tone="danger" variant="chip" style={{ fontWeight: 700 }}>−{m.pct_off < 1 ? '<1' : m.pct_off}%</Pill>
            {data.has_own_menu && (
              <div style={{ fontFamily: 'var(--mono)', fontSize: 10, minWidth: 90, textAlign: 'right', color: m.rival_cheaper ? 'var(--danger)' : m.vs_you_dollar == null ? 'var(--text-3)' : 'var(--text-2)', fontWeight: m.rival_cheaper ? 700 : 500, whiteSpace: 'nowrap' }}>
                {m.vs_you_dollar == null ? 'not carried' : `${m.vs_you_dollar < 0 ? '−' : '+'}${fmtMoney(Math.abs(m.vs_you_dollar))} vs you`}
              </div>
            )}
          </Card>
        ))}
      </div>
    </div>
  )
}
